<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useLoadWithRetry } from 'dashboard/composables/loadWithRetry';
import { useAdmin } from 'dashboard/composables/useAdmin';
import BaseBubble from './Base.vue';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { useMessageContext } from '../provider.js';
import { MESSAGE_TYPES } from '../constants';
import { downloadFile } from '@chatwoot/utils';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import WhatsAppStickerAPI from 'dashboard/api/whatsappStickers';

const { t } = useI18n();
const { filteredCurrentChatAttachments, attachments, messageType } =
  useMessageContext();
const { isAdmin } = useAdmin();

const attachment = computed(() => attachments.value[0]);
const { isLoaded, hasError, loadWithRetry } = useLoadWithRetry();
const showGallery = ref(false);
const isDownloading = ref(false);
const isSavingSticker = ref(false);

const canSaveSticker = computed(() => {
  return (
    isAdmin.value &&
    messageType.value === MESSAGE_TYPES.INCOMING &&
    attachment.value?.id
  );
});

onMounted(() => {
  if (attachment.value?.dataUrl) {
    loadWithRetry(attachment.value.dataUrl);
  }
});

const downloadAttachment = async () => {
  const { fileType, dataUrl, extension } = attachment.value;
  try {
    isDownloading.value = true;
    await downloadFile({ url: dataUrl, type: fileType, extension });
  } catch (error) {
    useAlert(t('GALLERY_VIEW.ERROR_DOWNLOADING'));
  } finally {
    isDownloading.value = false;
  }
};

const handleImageError = () => {
  hasError.value = true;
};

const saveSticker = async () => {
  if (!canSaveSticker.value) return;

  try {
    isSavingSticker.value = true;
    await WhatsAppStickerAPI.createFromAttachment(attachment.value.id);
    useAlert(t('CONVERSATION.SHARED_ATTACHMENT.STICKER_SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.SHARED_ATTACHMENT.STICKER_SAVE_ERROR')
    );
  } finally {
    isSavingSticker.value = false;
  }
};
</script>

<template>
  <BaseBubble
    class="overflow-hidden p-3"
    data-bubble-name="sticker"
    @click="showGallery = true"
  >
    <div
      class="mb-2 inline-flex items-center gap-1 rounded-full bg-n-alpha-2 px-2 py-1 text-xs font-medium text-n-slate-11"
    >
      <Icon icon="i-ph-sticker" class="size-3.5" />
      {{ t('CONVERSATION.SHARED_ATTACHMENT.STICKER_BADGE') }}
    </div>
    <div v-if="hasError" class="flex items-center gap-1 text-center rounded-lg">
      <Icon icon="i-lucide-circle-off" class="text-n-slate-11" />
      <p class="mb-0 text-n-slate-11">
        {{ $t('COMPONENTS.MEDIA.IMAGE_UNAVAILABLE') }}
      </p>
    </div>
    <div
      v-else-if="isLoaded"
      class="relative group rounded-xl overflow-hidden bg-transparent"
    >
      <img
        class="skip-context-menu mx-auto max-h-56 object-contain"
        :src="attachment.dataUrl"
        :width="attachment.width"
        :height="attachment.height"
      />
      <div class="mt-3 flex flex-wrap justify-end gap-2">
        <Button
          v-if="canSaveSticker"
          xs
          solid
          slate
          icon="i-ph-bookmark-simple"
          class="opacity-90"
          :title="t('CONVERSATION.SHARED_ATTACHMENT.STICKER_SAVE_ACTION')"
          :label="t('CONVERSATION.SHARED_ATTACHMENT.STICKER_SAVE_ACTION')"
          :is-loading="isSavingSticker"
          :disabled="isSavingSticker"
          @click.stop="saveSticker"
        />
        <Button xs solid slate icon="i-lucide-expand" class="opacity-90" />
        <Button
          xs
          solid
          slate
          icon="i-lucide-download"
          class="opacity-90"
          :is-loading="isDownloading"
          :disabled="isDownloading"
          @click.stop="downloadAttachment"
        />
      </div>
    </div>
  </BaseBubble>
  <GalleryView
    v-if="showGallery"
    v-model:show="showGallery"
    :attachment="useSnakeCase(attachment)"
    :all-attachments="filteredCurrentChatAttachments"
    @error="handleImageError"
    @close="() => (showGallery = false)"
  />
</template>
